import { useState } from 'react';
import type { Asset, ContentVariant } from '@kontentowo/shared';
import { api } from '../lib/api';
import { Button, ErrorAlert, Field, Modal } from './ui';

function autoVideoPrompt(variant: ContentVariant): string {
  return `Short vertical video for ${variant.platform}: ${variant.body.slice(0, 160)}`;
}

const POLL_INTERVAL = 4000;
const MAX_POLLS = 45; // ~3 min for KIE video jobs

/**
 * Per-variant video control: launches a KIE video job (or mock), polls until
 * ready, then attaches the mp4 asset to the variant.
 */
export function VariantVideoControl({
  brandId,
  contentId,
  variant,
  existing,
}: {
  brandId: string;
  contentId: string;
  variant: ContentVariant;
  existing?: Asset;
}) {
  const [asset, setAsset] = useState<Asset | null>(existing ?? null);
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState('');
  const [error, setError] = useState<unknown>(null);

  const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

  const generate = async () => {
    setBusy(true);
    setError(null);
    setPhase('Uruchamianie zadania…');
    try {
      const ratio = variant.aspectRatio === '9:16' || variant.aspectRatio === '1:1' || variant.aspectRatio === '16:9' ? variant.aspectRatio : '9:16';
      const job = await api.mediaVideoGenerate({
        brandId,
        contentId,
        variantId: variant.id,
        prompt: prompt.trim() || autoVideoPrompt(variant),
        aspectRatio: ratio,
        duration: 5,
      });
      setOpen(false);
      setPrompt('');

      for (let i = 0; i < MAX_POLLS; i++) {
        setPhase(`Generowanie wideo (${job.provider})… ${Math.round((i / MAX_POLLS) * 100)}%`);
        const res = await api.mediaVideoAttach({ jobId: job.jobId, brandId, contentId, variantId: variant.id });
        if (res.asset) {
          setAsset(res.asset);
          setPhase('');
          return;
        }
        if (res.status === 'FAILED') throw new Error(res.error ?? 'Video generation failed');
        await sleep(POLL_INTERVAL);
      }
      throw new Error('Video job timed out — sprawdź później w Media Library');
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
      setPhase('');
    }
  };

  return (
    <div style={{ marginTop: 10 }}>
      {asset && (
        <div style={{ marginBottom: 8 }}>
          {asset.mimeType.startsWith('video/') ? (
            <video src={asset.url ?? ''} controls style={{ maxWidth: '100%', maxHeight: 240, borderRadius: 8 }} />
          ) : (
            <span className="muted">{asset.mimeType}</span>
          )}
          <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
            {asset.provider} · {asset.mimeType}
          </div>
        </div>
      )}
      <Button size="sm" onClick={() => setOpen(true)} disabled={busy}>
        {asset ? 'Regeneruj wideo' : 'Generuj wideo'}
      </Button>
      {busy && (
        <span className="muted" style={{ fontSize: 12, marginLeft: 8 }}>
          {phase || '…'}
        </span>
      )}

      <Modal
        title={`Wideo dla ${variant.platform}`}
        open={open}
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button onClick={() => setOpen(false)}>Anuluj</Button>
            <Button variant="primary" onClick={generate} disabled={busy}>
              {busy ? 'Generowanie…' : 'Generuj'}
            </Button>
          </>
        }
      >
        <ErrorAlert error={error} />
        <Field label="Prompt" hint="Pusty = automatyczny na bazie treści wariantu. Generator: KIE (kling), async.">
          <textarea className="textarea" value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder={autoVideoPrompt(variant)} />
        </Field>
        <div className="muted" style={{ fontSize: 12 }}>
          Ratio: {variant.aspectRatio} · Provider: KIE.ai (kling-3.0/video) / mock
        </div>
      </Modal>
    </div>
  );
}